import React, { useState } from 'react';
import { renderParagraphs } from '../utils/textFormatters';
import ProjectBanner from '../components/project/ProjectBanner';
import ToggleableDetails from '../components/ToggleableDetails';
import useDocumentTitle from '../hooks/useDocumentTitle';
import mylesEventProject from '../data/mylesEventProject';

// Section Header Component
const SectionHeader = ({ title }) => (
  <div className="mb-8">
    <div className="inline-block">
      <h2 className="text-3xl font-bold text-[#2978C8] dark:text-[#4f9cf9]">{title}</h2>
      <div className="h-1 bg-[#2978C8] dark:bg-[#4f9cf9] mt-2"></div>
    </div>
  </div>
);

const MylesEventProjectPage = () => {
  useDocumentTitle('Myles Event');
  
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Function to handle image clicks
  const handleImageClick = (imageSrc) => {
    if (enlargedImage === imageSrc) {
      setEnlargedImage(null); // Shrink if clicked again
    } else {
      setEnlargedImage(imageSrc); // Enlarge if clicked first time
    }
  };

  return (
    <div className="project-page">
      <ProjectBanner project={mylesEventProject} />
      
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Overview Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#2978C8] dark:text-[#4f9cf9]">
            {mylesEventProject.titleIntro}
          </h2>
          {renderParagraphs(mylesEventProject.overview)}
        </div>
        
        {/* Contribution Section */}
        <div className="mb-16">
          <SectionHeader title={mylesEventProject.contributeTitle} />
          {renderParagraphs(mylesEventProject.contribution)}
        </div>
        
        {/* Features Section */}
        <div className="mb-16">
          <SectionHeader title="Features" />
          
          {mylesEventProject.features && mylesEventProject.features.map((feature, index) => (
            <div key={feature.id} className="mb-12">
              <h3 className="text-2xl font-bold text-[#294C6F] dark:text-[#4f9cf9] mb-4">{feature.title}</h3>
              
              {/* Pretext - Added for all features */}
              {feature.pretext && (
                <p className="text-lg text-[#333333] dark:text-white leading-relaxed mb-4">
                  {feature.pretext}
                </p>
              )}
              
              {/* Featured Image - Placed between pretext and description */}
              {feature.featuredImage && (
                <div className="my-6 w-full md:w-[80%] mx-auto">
                  <img
                    src={`${process.env.PUBLIC_URL}${feature.featuredImage}`}
                    alt={`Featured ${feature.title}`}
                    className="w-full h-auto rounded-xl shadow-lg"
                  />
                </div>
              )}
              
              {/* YouTube Video - Placed between pretext and description */}
              {feature.video && feature.video.includes('youtube') && (
                <div className="my-6 aspect-video w-full md:w-[70%] mx-auto">
                  <iframe
                    className="w-full h-full rounded-xl"
                    src={feature.video}
                    title={feature.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
              
              {/* Direct Video - Placed between pretext and description */}
              {feature.video && !feature.video.includes('youtube') && (
                <div className="my-6 aspect-video w-full md:w-[70%] mx-auto">
                  <video
                    className="w-full h-full rounded-xl"
                    src={`${process.env.PUBLIC_URL}${feature.video}`}
                    controls
                    autoPlay
                    muted
                    loop
                  ></video>
                </div>
              )}
              
              {/* Description */}
              <div className="text-lg text-[#333333] dark:text-white leading-relaxed mb-6">
                {renderParagraphs(feature.description)}
              </div>
              
              {/* Image Gallery with Toggleable Component - Moved below description */}
              {feature.images && feature.images.length > 0 && (
                <ToggleableDetails 
                  title="View Gallery" 
                  initialOpen={false}
                  customContent={
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {feature.images.map((image, imgIndex) => (
                        <div 
                          key={imgIndex} 
                          className={`cursor-pointer transition-all duration-300 ${
                            enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                          }`}
                          onClick={() => handleImageClick(`${process.env.PUBLIC_URL}${image}`)}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}${image}`}
                            alt={`Image ${imgIndex + 1}`}
                            className={`rounded-lg ${
                              enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                            }`}
                          />
                        </div>
                      ))}
                    </div>
                  }
                />
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Close enlarged image when clicking outside */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 z-40 bg-transparent"
          onClick={() => setEnlargedImage(null)}
        ></div>
      )}
    </div>
  );
};

export default MylesEventProjectPage; 